import { z } from 'zod';
import { uuid, paginationQuery, isoDatetime } from './common.js';

// ─── Enum ─────────────────────────────────────────────────────────────────
export const PackageStatus = {
  WAITING: 'WAITING',
  PICKED_UP: 'PICKED_UP',
  CANCELLED: 'CANCELLED',
} as const;
export type PackageStatus = (typeof PackageStatus)[keyof typeof PackageStatus];

const STATUSES = [
  PackageStatus.WAITING,
  PackageStatus.PICKED_UP,
  PackageStatus.CANCELLED,
] as const;

const photoUrl = z.string().min(1).max(2_900_000);   // ~2 MB as base64

// ─── Public shape ─────────────────────────────────────────────────────────
export const publicPackage = z.object({
  id: uuid,
  cluster_id: uuid,
  unit_id: uuid,
  logged_by_user_id: uuid,
  logged_by_name: z.string().optional(),
  unit_block: z.string().optional(),
  unit_number: z.string().optional(),
  recipient_name: z.string().nullable(),
  carrier: z.string().nullable(),
  description: z.string().nullable(),
  photo_url: z.string().nullable(),
  status: z.enum(STATUSES),
  picked_up_at: isoDatetime.nullable(),
  picked_up_by_user_id: uuid.nullable(),
  picked_up_by_name: z.string().optional(),
  picked_up_notes: z.string().nullable(),
  cancelled_at: isoDatetime.nullable(),
  cancelled_reason: z.string().nullable(),
  created_at: isoDatetime,
  updated_at: isoDatetime,
});
export type PublicPackage = z.infer<typeof publicPackage>;

// ─── Security request: log a new package ──────────────────────────────────
export const createPackageRequest = z.object({
  unit_id: uuid,
  recipient_name: z.string().min(1).max(100).trim().optional(),
  carrier: z.string().min(1).max(50).trim().optional(),
  description: z.string().max(300).trim().optional(),
  photo_url: photoUrl.optional(),
});
export type CreatePackageRequest = z.infer<typeof createPackageRequest>;

// ─── List queries ─────────────────────────────────────────────────────────
export const listPackagesQuery = paginationQuery.extend({
  status: z.enum(STATUSES).optional(),
});
export type ListPackagesQuery = z.infer<typeof listPackagesQuery>;

export const adminListPackagesQuery = listPackagesQuery.extend({
  unit_id: uuid.optional(),
  block: z.string().min(1).max(20).optional(),
});
export type AdminListPackagesQuery = z.infer<typeof adminListPackagesQuery>;

// ─── Mark-picked-up / cancel ──────────────────────────────────────────────
export const markPickedUpRequest = z.object({
  notes: z.string().max(200).optional(),
});

export const cancelPackageRequest = z.object({
  reason: z.string().min(1).max(200).trim(),
});
